// relative
import type { JsonSchemaNode } from './types';

// libraries
import { z } from 'zod';

export function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getTypeLabel(typeValue?: string | string[]): string {
  if (!typeValue) {
    return 'string';
  }

  return Array.isArray(typeValue) ? typeValue[0] : typeValue;
}

/**
 * Infers a `JsonSchemaNode` shape directly from an existing configuration
 * object. Used as a fallback for edit forms when the alarm type has no
 * `configurationSchema` on record but a previously-saved configuration
 * object exists, so the dynamic fields can still be rendered.
 */
export function inferSchemaFromObject(
  obj: Record<string, unknown>,
): JsonSchemaNode {
  const properties: Record<string, JsonSchemaNode> = {};

  for (const [key, nestedValue] of Object.entries(obj)) {
    if (isObject(nestedValue)) {
      properties[key] = inferSchemaFromObject(nestedValue);
      continue;
    }

    if (typeof nestedValue === 'number') {
      properties[key] = { type: 'number' };
      continue;
    }

    if (typeof nestedValue === 'boolean') {
      properties[key] = { type: 'boolean' };
      continue;
    }

    properties[key] = { type: 'string' };
  }

  return {
    type: 'object',
    properties,
  };
}

/**
 * Converts the backend's JSON Schema (`AlarmTypeDto.configurationSchema`)
 * into a real zod schema via zod v4's experimental `z.fromJSONSchema()`, so
 * the dynamic configuration fields get proper RHF+zod validation - including
 * nested `required` properties - instead of none at all.
 *
 * Only the "no schema at all" case falls back to a permissive `z.record()`.
 * A schema that fails to convert (e.g. an unsupported JSON Schema construct)
 * throws instead of silently falling back to that same permissive schema,
 * since that would otherwise disable configuration validation - including
 * `required` checks - without anyone noticing.
 */
export function buildConfigurationZodSchema(
  schema: JsonSchemaNode | null,
): z.ZodType<Record<string, unknown>, Record<string, unknown>> {
  if (!schema) {
    return z.record(z.string(), z.unknown());
  }

  return z.fromJSONSchema(
    schema as Parameters<typeof z.fromJSONSchema>[0],
  ) as z.ZodType<Record<string, unknown>, Record<string, unknown>>;
}

export function buildDefaultConfiguration(
  schema: JsonSchemaNode,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, childSchema] of Object.entries(schema.properties ?? {})) {
    if (childSchema.type === 'object' || childSchema.properties) {
      result[key] = buildDefaultConfiguration(childSchema);
    } else {
      const typeName = getTypeLabel(childSchema.type);
      result[key] = typeName === 'boolean' ? false : '';
    }
  }

  return result;
}

export function sanitizeForApi(value: unknown): unknown {
  if (value === '' || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeForApi(item))
      .filter((item) => item !== undefined);
  }

  if (isObject(value)) {
    const sanitizedObject: Record<string, unknown> = {};
    for (const [key, nestedValue] of Object.entries(value)) {
      const sanitizedValue = sanitizeForApi(nestedValue);
      if (sanitizedValue !== undefined) {
        sanitizedObject[key] = sanitizedValue;
      }
    }
    return sanitizedObject;
  }

  return value;
}
